/**
 * BusinessAgentsPage (spec 014 — Independent Business Agent, T118 / FR-103)
 *
 * Admin approval queue for Independent Business Agent applications:
 *   - Pending tab: applications with Approve / Reject actions (confirm dialog —
 *     approval unlocks the agent's accrued Locked earnings transactionally).
 *   - Approved tab: active agents with a Suspend action.
 *   - Suspended tab: suspended agents with a Reinstate (approve) action.
 *   - Rejected tab: rejected applications with an Approve action.
 *
 * Every action's confirm dialog captures an optional Reason, recorded in the
 * agent's lifecycle audit history. A Details action per row opens a modal with
 * earnings totals + ledger, recruit attribution, the umbrella downline (direct
 * recruits + per-recruit bounty/override earned), issued invites, and the
 * lifecycle timeline (GET /api/agents/admin/{spid}).
 *
 * Rows are removed optimistically on approve/reject/suspend (they leave the
 * current tab's status filter) and restored in place if the API call fails.
 *
 * A "Held earnings" tab lists earnings held by a cap (US-044-06) with Release /
 * Reverse (reason required). The detail modal adds Re-run accrual and, for a
 * linked agent, Clear linked account (reason required). After any of these
 * succeeds the Held list and the open detail refetch.
 *
 * States handled: loading, empty, error (with retry), action rollback banner.
 * Admin gating is enforced by the shell in App.tsx — this page trusts it.
 */

import { useCallback, useEffect, useReducer, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  BadgeCheck,
  CheckCircle,
  Info,
  RefreshCw,
  RotateCcw,
  UserCheck,
  UserX,
  X,
} from 'lucide-react';
import {
  getAgents,
  getAgentDetail,
  approveAgent,
  rejectAgent,
  suspendAgent,
  getHeldEarnings,
  getStandingOverrideSummary,
  releaseHeldEarning,
  reverseHeldEarning,
  rerunAllEarnings,
  clearAgentLink,
  writeOffShortfall,
  AgentAdminApiError,
  CLEAR_INSIDE_CLAWBACK_WINDOW,
  type WriteOffShortfallResult,
  type AgentEarningType,
  type HeldEarning,
  type StandingOverrideSummary,
  type RerunAllResult,
  type AgentAdminRow,
  type AgentAdminDetail,
  type AgentApprovalStatus,
} from '../../services/agentAdminService';
import {
  AgentActionConfirmModal,
  type AgentAdminAction,
} from '../../components/AgentActionConfirmModal';
import { ReasonDialog } from '../../components/ReasonDialog';

// ---------------------------------------------------------------------------
// State model
// ---------------------------------------------------------------------------

type AgentTab = AgentApprovalStatus;

const TABS: AgentTab[] = ['Pending', 'Approved', 'Suspended', 'Rejected'];

interface DetailState {
  spid: number;
  loading: boolean;
  data: AgentAdminDetail | null;
  error: string | null;
}

interface AgentsState {
  rows: AgentAdminRow[];
  tab: AgentTab;
  loading: boolean;
  /** Load (fetch) error — replaces the table. */
  error: string | null;
  /** Action failure after optimistic removal — shown as a dismissible banner. */
  actionError: string | null;
  /** Open confirm dialog, if any. */
  modal: { action: AgentAdminAction; agent: AgentAdminRow } | null;
  /** Open detail modal, if any. */
  detail: DetailState | null;
}

type AgentsAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; rows: AgentAdminRow[] }
  | { type: 'FETCH_ERROR'; error: string }
  | { type: 'SET_TAB'; tab: AgentTab }
  | { type: 'OPEN_MODAL'; action: AgentAdminAction; agent: AgentAdminRow }
  | { type: 'CLOSE_MODAL' }
  /** Optimistic removal — the row leaves the current tab immediately. */
  | { type: 'ACTION_COMMIT'; spid: number }
  /** Rollback — restore the row at its original index and surface the error. */
  | { type: 'ACTION_ROLLBACK'; row: AgentAdminRow; index: number; error: string }
  | { type: 'CLEAR_ACTION_ERROR' }
  | { type: 'DETAIL_OPEN'; spid: number }
  | { type: 'DETAIL_SUCCESS'; spid: number; data: AgentAdminDetail }
  | { type: 'DETAIL_ERROR'; spid: number; error: string }
  | { type: 'DETAIL_CLOSE' };

function reducer(state: AgentsState, action: AgentsAction): AgentsState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, rows: action.rows };
    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.error };
    case 'SET_TAB':
      // Bug 20254: the fetch effect keys on state.tab, so re-selecting the same tab (e.g.
      // coming back from Held earnings) refetches nothing - clearing rows here would leave
      // the tab empty.
      if (action.tab === state.tab) return { ...state, actionError: null };
      return { ...state, tab: action.tab, rows: [], actionError: null };
    case 'OPEN_MODAL':
      return { ...state, modal: { action: action.action, agent: action.agent } };
    case 'CLOSE_MODAL':
      return { ...state, modal: null };
    case 'ACTION_COMMIT':
      return {
        ...state,
        modal: null,
        actionError: null,
        rows: state.rows.filter((r) => r.spid !== action.spid),
      };
    case 'ACTION_ROLLBACK': {
      const rows = [...state.rows];
      rows.splice(Math.min(action.index, rows.length), 0, action.row);
      return { ...state, rows, actionError: action.error };
    }
    case 'CLEAR_ACTION_ERROR':
      return { ...state, actionError: null };
    case 'DETAIL_OPEN':
      return { ...state, detail: { spid: action.spid, loading: true, data: null, error: null } };
    case 'DETAIL_SUCCESS':
      if (state.detail?.spid !== action.spid) return state;
      return { ...state, detail: { ...state.detail, loading: false, data: action.data } };
    case 'DETAIL_ERROR':
      if (state.detail?.spid !== action.spid) return state;
      return { ...state, detail: { ...state.detail, loading: false, error: action.error } };
    case 'DETAIL_CLOSE':
      return { ...state, detail: null };
  }
}

const initialState: AgentsState = {
  rows: [],
  tab: 'Pending',
  loading: false,
  error: null,
  actionError: null,
  modal: null,
  detail: null,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function usd(amount: number): string {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

const WRITE_OFF_REASON_MAX = 400;

const WRITE_OFF_ERRORS: Record<string, string> = {
  'no-negative-balance': 'This agent has no negative balance, so there is nothing to write off.',
  'shortfall-not-coverable':
    'This negative balance has no reversal debt left to write off, so it cannot be written off. It is recovered only from the agent’s future earnings.',
  'write-off-conflict': 'The balance changed while writing off. Reload the agent and try again.',
};

const ACTION_VERBS: Record<
  AgentAdminAction,
  (spid: number, reason?: string) => Promise<AgentAdminRow>
> = {
  approve: approveAgent,
  reject: rejectAgent,
  suspend: suspendAgent,
};

const ACTION_LABELS: Record<AgentAdminAction, string> = {
  approve: 'approve',
  reject: 'reject',
  suspend: 'suspend',
};

/** Status → chip classes, matching the page's existing chip treatment. */
const STATUS_CHIP: Record<AgentApprovalStatus, string> = {
  Pending: 'bg-zinc-800 border-zinc-700 text-zinc-300',
  Approved: 'bg-emerald-950 border-emerald-800 text-emerald-300',
  Suspended: 'bg-amber-950 border-amber-800 text-amber-300',
  Rejected: 'bg-red-950 border-red-800 text-red-300',
};

/** Invite status → chip classes (same treatment as STATUS_CHIP). */
const INVITE_STATUS_CHIP: Record<string, string> = {
  Sent: 'bg-zinc-800 border-zinc-700 text-zinc-300',
  Claimed: 'bg-emerald-950 border-emerald-800 text-emerald-300',
  Expired: 'bg-amber-950 border-amber-800 text-amber-300',
  Revoked: 'bg-red-950 border-red-800 text-red-300',
};

const INVITE_KIND_LABELS: Record<string, string> = {
  EmailInvite: 'Email invite',
  DirectRegistration: 'Direct registration',
};

/** AgentEarningType names (what the API sends) and numeric codes -> readable labels (plan.md, US-044-06). */
const EARNING_TYPE_LABELS: Record<string, string> = {
  AppReferralActivation: 'App referral activation',
  AppReferralUpgrade: 'App referral upgrade',
  ServiceLeadFee: 'Service lead fee',
  AgentRecruitment: 'Agent recruitment',
  RecruiterOverride: 'IBA override',
  1: 'App referral activation',
  2: 'App referral upgrade',
  3: 'Service lead fee',
  4: 'Agent recruitment',
  5: 'IBA override',
};

function earningTypeLabel(type: AgentEarningType): string {
  return (
    EARNING_TYPE_LABELS[type] ??
    (typeof type === 'number' && type >= 5 ? 'IBA override' : `Type ${String(type)}`)
  );
}

/** Audit-log action strings -> readable labels; unknown actions render raw. */
const AUDIT_ACTION_LABELS: Record<string, string> = {
  HeldReverseCascadeFailed: 'Override reversal failed',
  RelinkedDifferentAccount: 'Relinked to a different Chelless account',
};

function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABELS[action] ?? action;
}

/** Approved support wording for the agent; verbatim, so it carries no digit, "days" or date. */
const CLEAR_REFUSED_SUPPORT_LINE =
  "The Chelless account linked to this agent profile can't be changed right now, because recently earned credit has been redeemed to it. The hold lifts on its own once the recently earned credit is past a waiting period. You can ask support again later. Credit not yet redeemed stays on the agent balance meanwhile.";

function formatEarliestClearDate(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

/** Rich rendering for the clawback 409; other failures keep the plain message. */
function renderClearError(err: unknown): ReactNode {
  if (!(err instanceof AgentAdminApiError) || err.code !== CLEAR_INSIDE_CLAWBACK_WINDOW) {
    return null;
  }
  const date = err.earliestClearAllowedAt
    ? formatEarliestClearDate(err.earliestClearAllowedAt)
    : null;
  return (
    <div className="flex flex-col gap-2">
      <p>{err.message}</p>
      {date && (
        <p>
          Earliest date a clear can be allowed: {date} -- internal, do not quote this date to the
          agent
        </p>
      )}
      <p>
        <span className="font-semibold">Tell the agent:</span> {CLEAR_REFUSED_SUPPORT_LINE}
      </p>
    </div>
  );
}

type CascadeWarning = { earning: HeldEarning; message: string };

const CASCADE_FAILED_FALLBACK =
  'The earning was reversed, but the recruiter override it generated is still standing. Reverse the same earning again to retry the override reversal.';

type ReleaseWarning = { earningId: number; message: string };

const RELEASE_FAILED_FALLBACK =
  'The earning was released, but the recruiter override held with it could not be released and is still held.';

type ReasonTarget =
  | { kind: 'release'; earning: HeldEarning }
  | { kind: 'reverse'; earning: HeldEarning }
  | { kind: 'clear'; spid: number };

const EMPTY_COPY: Record<AgentTab, string> = {
  Pending: 'No pending agent applications.',
  Approved: 'No approved agents yet.',
  Suspended: 'No suspended agents.',
  Rejected: 'No rejected applications.',
};

// ---------------------------------------------------------------------------
// Row component
// ---------------------------------------------------------------------------

interface RowProps {
  row: AgentAdminRow;
  tab: AgentTab;
  onAction: (action: AgentAdminAction, agent: AgentAdminRow) => void;
  onDetails: (agent: AgentAdminRow) => void;
}

function AgentRow({ row, tab, onAction, onDetails }: RowProps) {
  return (
    <tr className="border-t border-zinc-800 hover:bg-zinc-900/50 transition-colors">
      {/* Agent */}
      <td className="px-4 py-3 max-w-xs">
        <p className="text-zinc-200 text-sm font-medium break-words">{row.displayName}</p>
        <p className="text-zinc-500 text-xs mt-1 font-mono">SPID {row.spid}</p>
      </td>

      {/* Referral code */}
      <td className="px-4 py-3">
        {row.referralCode ? (
          <span className="text-xs bg-zinc-800 border border-zinc-700 px-2 py-0.5 rounded text-zinc-300 font-mono">
            {row.referralCode}
          </span>
        ) : (
          <span className="text-xs text-zinc-600">— not minted —</span>
        )}
      </td>

      {/* Applied at */}
      <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">
        {new Date(row.createdAt).toLocaleString()}
      </td>

      {/* Locked earnings preview */}
      <td className="px-4 py-3 whitespace-nowrap">
        <span
          className={
            row.lockedUsd > 0
              ? 'text-sm font-medium text-amber-300'
              : 'text-sm text-zinc-500'
          }
        >
          {usd(row.lockedUsd)}
        </span>
        <span className="text-xs text-zinc-600 ml-1.5">locked</span>
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-2">
          {tab === 'Pending' && (
            <>
              <button
                type="button"
                onClick={() => onAction('approve', row)}
                className="min-h-[48px] px-2.5 py-1 rounded bg-emerald-950 hover:bg-emerald-900 text-emerald-300 text-xs transition-colors flex items-center gap-1"
                aria-label={`Approve agent ${row.displayName}`}
              >
                <UserCheck size={12} aria-hidden="true" />
                Approve
              </button>
              <button
                type="button"
                onClick={() => onAction('reject', row)}
                className="min-h-[48px] px-2.5 py-1 rounded bg-red-950 hover:bg-red-900 text-red-300 text-xs transition-colors flex items-center gap-1"
                aria-label={`Reject agent ${row.displayName}`}
              >
                <UserX size={12} aria-hidden="true" />
                Reject
              </button>
            </>
          )}
          {tab === 'Approved' && (
            <button
              type="button"
              onClick={() => onAction('suspend', row)}
              className="min-h-[48px] px-2.5 py-1 rounded bg-amber-950 hover:bg-amber-900 text-amber-300 text-xs transition-colors flex items-center gap-1"
              aria-label={`Suspend agent ${row.displayName}`}
            >
              <UserX size={12} aria-hidden="true" />
              Suspend
            </button>
          )}
          {tab === 'Suspended' && (
            <button
              type="button"
              onClick={() => onAction('approve', row)}
              className="min-h-[48px] px-2.5 py-1 rounded bg-emerald-950 hover:bg-emerald-900 text-emerald-300 text-xs transition-colors flex items-center gap-1"
              aria-label={`Reinstate agent ${row.displayName}`}
            >
              <RotateCcw size={12} aria-hidden="true" />
              Reinstate
            </button>
          )}
          {tab === 'Rejected' && (
            <button
              type="button"
              onClick={() => onAction('approve', row)}
              className="min-h-[48px] px-2.5 py-1 rounded bg-emerald-950 hover:bg-emerald-900 text-emerald-300 text-xs transition-colors flex items-center gap-1"
              aria-label={`Approve agent ${row.displayName}`}
            >
              <UserCheck size={12} aria-hidden="true" />
              Approve
            </button>
          )}
          <button
            type="button"
            onClick={() => onDetails(row)}
            className="min-h-[48px] px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs transition-colors flex items-center gap-1"
            aria-label={`Details for agent ${row.displayName}`}
          >
            <Info size={12} aria-hidden="true" />
            Details
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Held earnings table
// ---------------------------------------------------------------------------

interface HeldTableProps {
  rows: HeldEarning[];
  onAct: (kind: 'release' | 'reverse', earning: HeldEarning) => void;
}

function HeldEarningsTable({ rows, onAct }: HeldTableProps) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-zinc-500 gap-2">
        <CheckCircle size={40} className="text-zinc-700" aria-hidden="true" />
        <p className="text-sm">No held earnings.</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-zinc-800 overflow-x-auto">
      <table className="w-full text-sm" aria-label="Held earnings">
        <thead>
          <tr className="bg-zinc-900 text-zinc-400 text-left text-xs uppercase tracking-wide">
            <th scope="col" className="px-4 py-3 font-medium">Agent</th>
            <th scope="col" className="px-4 py-3 font-medium">Type</th>
            <th scope="col" className="px-4 py-3 font-medium">Amount</th>
            <th scope="col" className="px-4 py-3 font-medium">Cap tripped</th>
            <th scope="col" className="px-4 py-3 font-medium">Date</th>
            <th scope="col" className="px-4 py-3 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.earningId}
              className="border-t border-zinc-800 hover:bg-zinc-900/50 transition-colors"
            >
              <td className="px-4 py-3">
                <p className="text-zinc-200 text-sm font-medium break-words">{row.agentName}</p>
                <p className="text-zinc-500 text-xs mt-1 font-mono">SPID {row.spid}</p>
              </td>
              <td className="px-4 py-3 text-xs text-zinc-300">{earningTypeLabel(row.earningType)}</td>
              <td className="px-4 py-3 text-sm text-zinc-200 whitespace-nowrap">
                {usd(row.amountUsd)}
              </td>
              <td className="px-4 py-3 text-xs text-zinc-400">{row.capTripped ?? '—'}</td>
              <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">
                {new Date(row.createdAt).toLocaleString()}
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onAct('release', row)}
                    className="min-h-[48px] px-2.5 py-1 rounded bg-emerald-950 hover:bg-emerald-900 text-emerald-300 text-xs transition-colors"
                    aria-label={`Release held earning ${row.earningId} for ${row.agentName}`}
                  >
                    Release
                  </button>
                  <button
                    type="button"
                    onClick={() => onAct('reverse', row)}
                    className="min-h-[48px] px-2.5 py-1 rounded bg-red-950 hover:bg-red-900 text-red-300 text-xs transition-colors"
                    aria-label={`Reverse held earning ${row.earningId} for ${row.agentName}`}
                  >
                    Reverse
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail modal — earnings totals + ledger, attribution, lifecycle timeline
// ---------------------------------------------------------------------------

interface AgentDetailModalProps {
  detail: DetailState;
  onClose: () => void;
  onRetry: (spid: number) => void;
  onRerun: (spid: number) => Promise<RerunAllResult>;
  onClearLink: (spid: number) => void;
  onWriteOff: (spid: number, reason: string) => Promise<WriteOffShortfallResult>;
  /** Opens the reverse reason dialog for a Reversed earning whose override is still standing. */
  onRetryOverride: (earning: HeldEarning) => void;
  /** True while a reason dialog sits on top - ESC then belongs to that dialog. */
  escapeDisabled: boolean;
}

function AgentDetailModal({
  detail,
  onClose,
  onRetry,
  onRerun,
  onClearLink,
  onWriteOff,
  onRetryOverride,
  escapeDisabled,
}: AgentDetailModalProps) {
  const [rerun, setRerun] = useState<{
    busy: boolean;
    result: RerunAllResult | null;
    error: string | null;
  }>({ busy: false, result: null, error: null });

  async function handleRerun() {
    setRerun({ busy: true, result: null, error: null });
    try {
      const result = await onRerun(detail.spid);
      setRerun({ busy: false, result, error: null });
    } catch (err) {
      setRerun({
        busy: false,
        result: null,
        error: err instanceof Error ? err.message : 'Re-run failed',
      });
    }
  }

  const [writeOff, setWriteOff] = useState<{
    open: boolean;
    reason: string;
    busy: boolean;
    error: string | null;
  }>({ open: false, reason: '', busy: false, error: null });

  // Negative balance a completed write-off could not cover; null when it covered everything.
  const [uncoveredUsd, setUncoveredUsd] = useState<number | null>(null);

  async function handleWriteOff() {
    const reason = writeOff.reason.trim();
    if (!reason) return;
    setWriteOff((w) => ({ ...w, busy: true, error: null }));
    try {
      const result = await onWriteOff(detail.spid, reason);
      setUncoveredUsd(result.uncoveredUsd > 0 ? result.uncoveredUsd : null);
      setWriteOff({ open: false, reason: '', busy: false, error: null });
    } catch (err) {
      const code = err instanceof Error ? err.message : '';
      setWriteOff((w) => ({
        ...w,
        busy: false,
        error: WRITE_OFF_ERRORS[code] ?? (code || 'Write-off failed'),
      }));
    }
  }

  // ESC closes the detail modal
  useEffect(() => {
    if (escapeDisabled) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, escapeDisabled]);

  const data = detail.data;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 z-40" aria-hidden="true" onClick={onClose} />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="agent-detail-title"
          className="w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl flex flex-col gap-4 p-6"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <h2 id="agent-detail-title" className="text-sm font-semibold text-zinc-100">
              {data ? (
                <>
                  {data.profile.displayName}
                  <span className="ml-2 text-xs font-normal text-zinc-500 font-mono">
                    SPID {data.profile.spid} · {data.profile.approvalStatus}
                  </span>
                </>
              ) : (
                `Agent detail — SPID ${detail.spid}`
              )}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close agent details"
              className="text-zinc-500 hover:text-zinc-200 transition-colors"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>

          {/* Loading state */}
          {detail.loading && (
            <div
              className="flex items-center justify-center py-12 text-zinc-500"
              role="status"
              aria-label="Loading agent details"
            >
              <RefreshCw className="w-5 h-5 mr-3 motion-safe:animate-spin" aria-hidden="true" />
              Loading agent details…
            </div>
          )}

          {/* Error state */}
          {detail.error && !detail.loading && (
            <div
              className="flex items-start gap-3 p-4 rounded-lg bg-red-950 border border-red-800 text-red-300"
              role="alert"
            >
              <AlertTriangle size={18} className="shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="font-medium text-sm">Failed to load agent details</p>
                <p className="text-xs text-red-400 mt-0.5">{detail.error}</p>
                <button
                  type="button"
                  onClick={() => onRetry(detail.spid)}
                  className="mt-2 text-xs text-red-300 underline hover:text-red-100"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {data && !detail.loading && (
            <>
              {/* Recruit attribution */}
              {data.profile.referredByAgentSpid !== null && (
                <p className="text-xs text-violet-300 bg-violet-950/40 border border-violet-500/30 rounded-lg px-3 py-2">
                  Recruited by agent SPID {data.profile.referredByAgentSpid}
                  {data.referredByCode ? ` via code ${data.referredByCode}` : ''}
                </p>
              )}

              {/* Linked account + accrual recovery (US-044-06) */}
              <section aria-label="Linked account and accrual">
                <h3 className="text-xs uppercase tracking-wide text-zinc-500 font-medium mb-2">
                  Account and accrual
                </h3>
                <div className="flex flex-col gap-2">
                  <p className="text-xs text-zinc-300">
                    {data.profile.mainAppProfileId
                      ? `Linked Chelless account: ${data.profile.mainAppProfileId}`
                      : 'Not linked'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void handleRerun()}
                      disabled={rerun.busy}
                      className="min-h-[48px] px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 text-zinc-300 text-xs transition-colors flex items-center gap-1"
                      aria-label="Re-run accrual for this agent"
                    >
                      <RefreshCw size={12} aria-hidden="true" />
                      {rerun.busy ? 'Re-running…' : 'Re-run accrual'}
                    </button>
                    {data.profile.mainAppProfileId && (
                      <button
                        type="button"
                        onClick={() => onClearLink(data.profile.spid)}
                        className="min-h-[48px] px-2.5 py-1 rounded bg-amber-950 hover:bg-amber-900 text-amber-300 text-xs transition-colors"
                        aria-label="Clear linked account for this agent"
                      >
                        Clear linked account
                      </button>
                    )}
                  </div>
                  {rerun.result && (
                    <p className="text-xs text-emerald-300" role="status">
                      {rerun.result.rowsAdded} {rerun.result.rowsAdded === 1 ? 'row' : 'rows'} added,{' '}
                      {rerun.result.rowsSkipped} skipped
                    </p>
                  )}
                  {rerun.error && (
                    <p className="text-xs text-red-300" role="alert">
                      {rerun.error}
                    </p>
                  )}
                </div>
              </section>

              {/* Earnings totals */}
              <section aria-label="Earnings totals">
                <h3 className="text-xs uppercase tracking-wide text-zinc-500 font-medium mb-2">
                  Earnings
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2">
                    <p className="text-xs text-zinc-500">Locked</p>
                    <p className="text-sm font-medium text-amber-300">
                      {usd(data.earnings.totals.lockedUsd)}
                    </p>
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2">
                    <p className="text-xs text-zinc-500">Available</p>
                    <p className="text-sm font-medium text-emerald-300">
                      {usd(data.earnings.totals.availableUsd)}
                    </p>
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2">
                    <p className="text-xs text-zinc-500">Redeemed</p>
                    <p className="text-sm font-medium text-zinc-300">
                      {usd(data.earnings.totals.redeemedUsd)}
                    </p>
                  </div>
                </div>
              </section>

              {/* Earnings ledger */}
              <section aria-label="Earnings ledger">
                <h3 className="text-xs uppercase tracking-wide text-zinc-500 font-medium mb-2">
                  Ledger
                </h3>
                {data.earnings.entries.length === 0 ? (
                  <p className="text-xs text-zinc-600">No earnings entries.</p>
                ) : (
                  <div className="rounded-lg border border-zinc-800 overflow-x-auto">
                    <table className="w-full text-sm" aria-label="Earnings ledger entries">
                      <thead>
                        <tr className="bg-zinc-950 text-zinc-500 text-left text-xs uppercase tracking-wide">
                          <th scope="col" className="px-3 py-2 font-medium">Entry</th>
                          <th scope="col" className="px-3 py-2 font-medium">Amount</th>
                          <th scope="col" className="px-3 py-2 font-medium">Status</th>
                          <th scope="col" className="px-3 py-2 font-medium">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.earnings.entries.map((entry) => (
                          <tr key={entry.id} className="border-t border-zinc-800">
                            <td className="px-3 py-2">
                              <p className="text-xs text-zinc-300">
                                {entry.description ?? earningTypeLabel(entry.earningType)}
                              </p>
                              <p className="text-xs text-zinc-600 font-mono">{entry.sourceReference}</p>
                            </td>
                            <td className="px-3 py-2 text-xs text-zinc-200 whitespace-nowrap">
                              {usd(entry.amountUsd)}
                            </td>
                            <td className="px-3 py-2">
                              <span className="text-xs bg-zinc-800 border border-zinc-700 px-2 py-0.5 rounded text-zinc-400">
                                {entry.status}
                              </span>
                              {entry.status === 'Reversed' && entry.hasStandingOverride && data && (
                                <div className="mt-1 flex flex-col items-start gap-1">
                                  <span className="text-xs bg-amber-950 border border-amber-800 px-2 py-0.5 rounded text-amber-300">
                                    Override still standing
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      onRetryOverride({
                                        earningId: entry.id,
                                        spid: data.profile.spid,
                                        agentName: data.profile.displayName,
                                        earningType: entry.earningType,
                                        amountUsd: entry.amountUsd,
                                        capTripped: null,
                                        createdAt: entry.createdAt,
                                      })
                                    }
                                    aria-label={`Retry override reversal for earning ${entry.id}`}
                                    className="min-h-[48px] px-3 rounded text-sm bg-amber-800 text-amber-50 hover:bg-amber-700 transition-colors"
                                  >
                                    Retry override reversal
                                  </button>
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs text-zinc-500 whitespace-nowrap">
                              {new Date(entry.createdAt).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Shortfall write-offs (US-26-GR-62) */}
              <section aria-label="Write-offs">
                <h3 className="text-xs uppercase tracking-wide text-zinc-500 font-medium mb-2">
                  Write-offs
                </h3>
                {data.earnings.totals.availableUsd < 0 && !writeOff.open && (
                  <button
                    type="button"
                    onClick={() => {
                      setUncoveredUsd(null);
                      setWriteOff((w) => ({ ...w, open: true, error: null }));
                    }}
                    className="min-h-[48px] mb-2 px-2.5 py-1 rounded bg-amber-950 hover:bg-amber-900 text-amber-300 text-xs transition-colors"
                    aria-label="Write off shortfall for this agent"
                  >
                    Write off shortfall
                  </button>
                )}
                {uncoveredUsd !== null && (
                  <p
                    className="text-xs text-amber-300 mb-2 rounded-lg border border-amber-500/30 bg-amber-950/20 p-3"
                    role="status"
                  >
                    Write-off recorded, but {usd(uncoveredUsd)} of the negative balance remains
                    uncovered. It is recovered only from the agent&rsquo;s future earnings.
                  </p>
                )}
                {data.earnings.totals.availableUsd < 0 && writeOff.open && (
                  <form
                    className="flex flex-col gap-2 mb-2 rounded-lg border border-amber-500/30 bg-amber-950/20 p-3"
                    aria-label="Write off shortfall"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void handleWriteOff();
                    }}
                  >
                    <p className="text-xs text-zinc-300">
                      Writes off the {usd(Math.abs(data.earnings.totals.availableUsd))} the agent
                      cannot repay. The reason is recorded with the write-off.
                    </p>
                    <label htmlFor="write-off-reason" className="text-xs text-zinc-400">
                      Reason (required)
                    </label>
                    <textarea
                      id="write-off-reason"
                      value={writeOff.reason}
                      maxLength={WRITE_OFF_REASON_MAX}
                      rows={3}
                      disabled={writeOff.busy}
                      onChange={(e) => setWriteOff((w) => ({ ...w, reason: e.target.value }))}
                      className="w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1 text-xs text-zinc-200"
                    />
                    <p className="text-xs text-zinc-500" aria-live="polite">
                      {writeOff.reason.length}/{WRITE_OFF_REASON_MAX}
                    </p>
                    {writeOff.error && (
                      <p className="text-xs text-red-300" role="alert">
                        {writeOff.error}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={writeOff.busy || writeOff.reason.trim().length === 0}
                        aria-label="Confirm write-off"
                        className="min-h-[48px] px-2.5 py-1 rounded bg-amber-700 hover:bg-amber-600 disabled:opacity-40 text-white text-xs transition-colors"
                      >
                        {writeOff.busy ? 'Writing off…' : 'Confirm write-off'}
                      </button>
                      <button
                        type="button"
                        disabled={writeOff.busy}
                        onClick={() =>
                          setWriteOff({ open: false, reason: '', busy: false, error: null })
                        }
                        aria-label="Cancel write-off"
                        className="min-h-[48px] px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 text-zinc-300 text-xs transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
                {data.writeOffs.length === 0 ? (
                  <p className="text-xs text-zinc-600">No write-offs.</p>
                ) : (
                  <div className="rounded-lg border border-zinc-800 overflow-x-auto">
                    <table className="w-full text-sm" aria-label="Write-offs list">
                      <thead>
                        <tr className="bg-zinc-950 text-zinc-500 text-left text-xs uppercase tracking-wide">
                          <th scope="col" className="px-3 py-2 font-medium">Amount</th>
                          <th scope="col" className="px-3 py-2 font-medium">Reason</th>
                          <th scope="col" className="px-3 py-2 font-medium">By</th>
                          <th scope="col" className="px-3 py-2 font-medium">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.writeOffs.map((w) => (
                          <tr key={w.id} className="border-t border-zinc-800">
                            <td className="px-3 py-2 text-xs text-zinc-200 whitespace-nowrap">
                              {usd(w.amountUsd)}
                            </td>
                            <td className="px-3 py-2 text-xs text-zinc-300 break-words">
                              {w.reason}
                            </td>
                            <td className="px-3 py-2 text-xs text-zinc-500">{w.actor}</td>
                            <td className="px-3 py-2 text-xs text-zinc-500 whitespace-nowrap">
                              {new Date(w.createdAt).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Downline — direct recruits + what this agent earned from each */}
              <section aria-label="Downline">
                <h3 className="text-xs uppercase tracking-wide text-zinc-500 font-medium mb-2">
                  Downline
                </h3>
                {data.downline.length === 0 ? (
                  <p className="text-xs text-zinc-600">No recruits yet.</p>
                ) : (
                  <div className="rounded-lg border border-zinc-800 overflow-x-auto">
                    <table className="w-full text-sm" aria-label="Downline recruits">
                      <thead>
                        <tr className="bg-zinc-950 text-zinc-500 text-left text-xs uppercase tracking-wide">
                          <th scope="col" className="px-3 py-2 font-medium">Recruit</th>
                          <th scope="col" className="px-3 py-2 font-medium">Status</th>
                          <th scope="col" className="px-3 py-2 font-medium">Joined</th>
                          <th scope="col" className="px-3 py-2 font-medium">Bounty</th>
                          <th scope="col" className="px-3 py-2 font-medium">Override</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.downline.map((recruit) => (
                          <tr key={recruit.spid} className="border-t border-zinc-800">
                            <td className="px-3 py-2">
                              <p className="text-xs text-zinc-300 break-words">
                                {recruit.displayName}
                              </p>
                              <p className="text-xs text-zinc-600 font-mono">
                                SPID {recruit.spid}
                              </p>
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`text-xs border px-2 py-0.5 rounded ${STATUS_CHIP[recruit.approvalStatus]}`}
                              >
                                {recruit.approvalStatus}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-zinc-500 whitespace-nowrap">
                              {new Date(recruit.joinedAt).toLocaleDateString()}
                            </td>
                            <td className="px-3 py-2 text-xs text-zinc-200 whitespace-nowrap">
                              {usd(recruit.bountyUsd)}
                            </td>
                            <td className="px-3 py-2 text-xs text-zinc-200 whitespace-nowrap">
                              {usd(recruit.overrideUsd)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-zinc-700 bg-zinc-950">
                          <td className="px-3 py-2 text-xs font-medium text-zinc-400" colSpan={3}>
                            Total ({data.downline.length}{' '}
                            {data.downline.length === 1 ? 'recruit' : 'recruits'})
                          </td>
                          <td className="px-3 py-2 text-xs font-medium text-emerald-300 whitespace-nowrap">
                            {usd(data.downline.reduce((sum, r) => sum + r.bountyUsd, 0))}
                          </td>
                          <td className="px-3 py-2 text-xs font-medium text-emerald-300 whitespace-nowrap">
                            {usd(data.downline.reduce((sum, r) => sum + r.overrideUsd, 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </section>

              {/* Invites issued by this agent */}
              <section aria-label="Invites">
                <h3 className="text-xs uppercase tracking-wide text-zinc-500 font-medium mb-2">
                  Invites
                </h3>
                {data.invites.length === 0 ? (
                  <p className="text-xs text-zinc-600">No invites issued.</p>
                ) : (
                  <div className="rounded-lg border border-zinc-800 overflow-x-auto">
                    <table className="w-full text-sm" aria-label="Recruiting invites">
                      <thead>
                        <tr className="bg-zinc-950 text-zinc-500 text-left text-xs uppercase tracking-wide">
                          <th scope="col" className="px-3 py-2 font-medium">Invitee</th>
                          <th scope="col" className="px-3 py-2 font-medium">Kind</th>
                          <th scope="col" className="px-3 py-2 font-medium">Status</th>
                          <th scope="col" className="px-3 py-2 font-medium">Created</th>
                          <th scope="col" className="px-3 py-2 font-medium">Expires</th>
                          <th scope="col" className="px-3 py-2 font-medium">Claimed SPID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.invites.map((invite) => (
                          <tr key={invite.id} className="border-t border-zinc-800">
                            <td className="px-3 py-2">
                              {invite.displayName && (
                                <p className="text-xs text-zinc-300 break-words">
                                  {invite.displayName}
                                </p>
                              )}
                              <p className="text-xs text-zinc-500 font-mono break-all">
                                {invite.email ?? '—'}
                              </p>
                            </td>
                            <td className="px-3 py-2 text-xs text-zinc-400 whitespace-nowrap">
                              {INVITE_KIND_LABELS[invite.kind] ?? invite.kind}
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`text-xs border px-2 py-0.5 rounded ${
                                  INVITE_STATUS_CHIP[invite.status] ??
                                  'bg-zinc-800 border-zinc-700 text-zinc-300'
                                }`}
                              >
                                {invite.status}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-zinc-500 whitespace-nowrap">
                              {new Date(invite.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-3 py-2 text-xs text-zinc-500 whitespace-nowrap">
                              {invite.expiresAt
                                ? new Date(invite.expiresAt).toLocaleDateString()
                                : '—'}
                            </td>
                            <td className="px-3 py-2 text-xs text-zinc-400 font-mono whitespace-nowrap">
                              {invite.claimedSpid ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Lifecycle history timeline */}
              <section aria-label="Lifecycle history">
                <h3 className="text-xs uppercase tracking-wide text-zinc-500 font-medium mb-2">
                  History
                </h3>
                {data.history.length === 0 ? (
                  <p className="text-xs text-zinc-600">No lifecycle history.</p>
                ) : (
                  <ol className="flex flex-col gap-2">
                    {data.history.map((entry) => (
                      <li
                        key={entry.id}
                        className="rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 flex flex-col gap-0.5"
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="text-xs font-medium text-zinc-200">
                            {entry.action === 'RelinkedDifferentAccount' ? (
                              <span className="rounded bg-amber-950 border border-amber-800 px-1.5 py-0.5 text-amber-200">
                                {auditActionLabel(entry.action)}
                              </span>
                            ) : (
                              auditActionLabel(entry.action)
                            )}
                            {(entry.fromStatus || entry.toStatus) && (
                              <span className="ml-2 font-normal text-zinc-500">
                                {entry.fromStatus ?? '—'} → {entry.toStatus ?? '—'}
                              </span>
                            )}
                          </span>
                          <span className="text-xs text-zinc-600 whitespace-nowrap">
                            {new Date(entry.createdAt).toLocaleString()}
                          </span>
                        </div>
                        {entry.actor && (
                          <p className="text-xs text-zinc-500">by {entry.actor}</p>
                        )}
                        {entry.reason && (
                          <p className="text-xs text-zinc-400 italic">"{entry.reason}"</p>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/** "3 days", "5 hours", "under a minute" from an ISO timestamp. */
function formatAge(sinceUtc: string, now: number = Date.now()): string {
  const ms = now - new Date(sinceUtc).getTime();
  if (Number.isNaN(ms) || ms < 60_000) return 'under a minute';
  const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return plural(minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return plural(hours, 'hour');
  return plural(Math.floor(hours / 24), 'day');
}

const OVERRIDE_KIND_LABEL: Record<'ReversedParent' | 'ReleasedParent', string> = {
  ReversedParent: 'parent reversed',
  ReleasedParent: 'release failed',
};

function StandingOverridesPanel({
  summary,
  loading,
  error,
  onOpenAgent,
}: {
  summary: StandingOverrideSummary | null;
  loading: boolean;
  error: string | null;
  onOpenAgent: (spid: number) => void;
}) {
  return (
    <section
      aria-label="Standing recruiter overrides"
      data-testid="standing-overrides-panel"
      className="p-4 rounded-lg bg-zinc-900 border border-zinc-800 space-y-2"
    >
      <h2 className="text-sm font-medium text-zinc-200">Standing recruiter overrides</h2>
      {error && (
        <p role="alert" className="text-sm text-red-400">
          Could not load standing overrides: {error}
        </p>
      )}
      {!error && !summary && loading && (
        <p role="status" className="text-sm text-zinc-500">
          Checking standing overrides...
        </p>
      )}
      {!error && summary && summary.standingCount === 0 && (
        <p className="text-sm text-zinc-400" data-testid="standing-overrides-count">
          No standing overrides
        </p>
      )}
      {!error && summary && summary.standingCount > 0 && (
        <>
          <p className="text-sm text-amber-300">
            <span data-testid="standing-overrides-count">
              {summary.standingCount} standing {summary.standingCount === 1 ? 'override' : 'overrides'}
            </span>
            {summary.oldestSinceUtc && <> · oldest {formatAge(summary.oldestSinceUtc)}</>}
            {' · '}
            {summary.spentCount} already spent
          </p>
          <ul className="space-y-1" aria-label="Agents with standing overrides">
            {summary.items.map((item) => (
              <li key={item.overrideEarningId}>
                <button
                  type="button"
                  onClick={() => onOpenAgent(item.agentSpid)}
                  className="min-h-[48px] w-full text-left px-3 rounded text-sm bg-zinc-800 text-zinc-200 hover:bg-zinc-700 transition-colors"
                >
                  <span className="text-violet-300 underline">
                    {item.agentName ?? `Agent ${item.agentSpid}`}
                  </span>
                  {' · '}
                  {OVERRIDE_KIND_LABEL[item.kind]}
                  {' · '}${item.amountUsd.toFixed(2)}
                  {item.spent && ' · spent'}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export function BusinessAgentsPage() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [showHeld, setShowHeld] = useState(false);
  const [held, setHeld] = useState<{ rows: HeldEarning[]; loading: boolean; error: string | null }>(
    { rows: [], loading: false, error: null }
  );
  const [standing, setStanding] = useState<{
    summary: StandingOverrideSummary | null;
    loading: boolean;
    error: string | null;
  }>({ summary: null, loading: true, error: null });
  const [reasonTarget, setReasonTarget] = useState<ReasonTarget | null>(null);
  // The reversed earning leaves the Held list, so the retry has no row; this panel carries it.
  // One item per earning so a second failure never hides the first.
  const [cascadeWarnings, setCascadeWarnings] = useState<CascadeWarning[]>([]);
  const [releaseWarnings, setReleaseWarnings] = useState<ReleaseWarning[]>([]);

  // Story 19992 follow-up / [19985]: the response for a tab the user has already left can
  // resolve AFTER the active tab's own response (DEV cold start reorders two in-flight
  // fetches easily) and FETCH_SUCCESS replaces state.rows unconditionally — the stale rows
  // then fail the client-side tab filter below and render as an empty tab. A cancel token
  // scoped to this effect run lets a late arrival recognise it is stale and no-op instead of
  // overwriting the correct, already-loaded rows.
  const loadAgents = useCallback(async (tab: AgentTab, cancelToken: { cancelled: boolean }) => {
    dispatch({ type: 'FETCH_START' });
    try {
      const rows = await getAgents(tab);
      if (cancelToken.cancelled) return;
      dispatch({ type: 'FETCH_SUCCESS', rows });
    } catch (err) {
      if (cancelToken.cancelled) return;
      dispatch({
        type: 'FETCH_ERROR',
        error: err instanceof Error ? err.message : 'Failed to load agents',
      });
    }
  }, []);

  useEffect(() => {
    const cancelToken = { cancelled: false };
    void loadAgents(state.tab, cancelToken);
    return () => {
      cancelToken.cancelled = true;
    };
  }, [loadAgents, state.tab]);

  const openDetail = useCallback(async (spid: number) => {
    dispatch({ type: 'DETAIL_OPEN', spid });
    try {
      const data = await getAgentDetail(spid);
      dispatch({ type: 'DETAIL_SUCCESS', spid, data });
    } catch (err) {
      dispatch({
        type: 'DETAIL_ERROR',
        spid,
        error: err instanceof Error ? err.message : 'Failed to load agent details',
      });
    }
  }, []);

  // Detail refetch that keeps the modal on screen (no loading flip).
  const refreshDetail = useCallback(async (spid: number) => {
    try {
      const data = await getAgentDetail(spid);
      dispatch({ type: 'DETAIL_SUCCESS', spid, data });
    } catch (err) {
      dispatch({
        type: 'DETAIL_ERROR',
        spid,
        error: err instanceof Error ? err.message : 'Failed to load agent details',
      });
    }
  }, []);

  const loadHeld = useCallback(async () => {
    setHeld((h) => ({ ...h, loading: true, error: null }));
    try {
      const rows = await getHeldEarnings();
      setHeld({ rows, loading: false, error: null });
    } catch (err) {
      setHeld((h) => ({
        ...h,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load held earnings',
      }));
    }
  }, []);

  const loadStanding = useCallback(async () => {
    setStanding((s) => ({ ...s, loading: true, error: null }));
    try {
      const summary = await getStandingOverrideSummary();
      setStanding({ summary, loading: false, error: null });
    } catch (err) {
      setStanding((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load standing overrides',
      }));
    }
  }, []);

  useEffect(() => {
    void loadStanding();
  }, [loadStanding]);

  useEffect(() => {
    if (showHeld) void loadHeld();
  }, [showHeld, loadHeld]);

  const detailSpid = state.detail?.spid ?? null;

  /** After a release / reverse / re-run / clear succeeds: Held list + open detail refetch. */
  const refetchAfterSuccess = useCallback(
    async (spid: number | null) => {
      await Promise.all([
        loadHeld(),
        loadStanding(),
        spid === null ? Promise.resolve() : refreshDetail(spid),
      ]);
    },
    [loadHeld, loadStanding, refreshDetail]
  );

  const handleRerun = useCallback(
    async (spid: number) => {
      const result = await rerunAllEarnings(spid);
      await refetchAfterSuccess(spid);
      return result;
    },
    [refetchAfterSuccess]
  );

  const handleWriteOff = useCallback(
    async (spid: number, reason: string) => {
      const result = await writeOffShortfall(spid, reason);
      await refreshDetail(spid);
      return result;
    },
    [refreshDetail]
  );

  const handleReasonSubmit = useCallback(
    async (reason: string) => {
      if (!reasonTarget) return;
      if (reasonTarget.kind === 'release') {
        const releasedId = reasonTarget.earning.earningId;
        const result = await releaseHeldEarning(releasedId, reason);
        setReleaseWarnings((prev) => {
          const others = prev.filter((w) => w.earningId !== releasedId);
          if (result.overrideRelease !== 'Failed') return others;
          return [
            ...others,
            { earningId: releasedId, message: result.warning?.trim() || RELEASE_FAILED_FALLBACK },
          ];
        });
      } else if (reasonTarget.kind === 'reverse') {
        const earning = reasonTarget.earning;
        let result: Awaited<ReturnType<typeof reverseHeldEarning>>;
        try {
          result = await reverseHeldEarning(earning.earningId, reason);
        } catch (err) {
          // e.g. 409 "already reversed": the ledger row must reflect the server now.
          if (detailSpid !== null) void refreshDetail(detailSpid);
          throw err;
        }
        const message = result.warning?.trim() || CASCADE_FAILED_FALLBACK;
        setCascadeWarnings((prev) => {
          const others = prev.filter((w) => w.earning.earningId !== earning.earningId);
          if (result.overrideCascade !== 'Failed') return others;
          const existing = prev.findIndex((w) => w.earning.earningId === earning.earningId);
          if (existing < 0) return [...prev, { earning, message }];
          return prev.map((w, i) => (i === existing ? { earning, message } : w));
        });
      } else {
        await clearAgentLink(reasonTarget.spid, reason);
      }
      setReasonTarget(null);
      await refetchAfterSuccess(detailSpid);
    },
    [reasonTarget, refetchAfterSuccess, refreshDetail, detailSpid]
  );

  const handleConfirm = useCallback(
    async (reason?: string) => {
      const modal = state.modal;
      if (!modal) return;
      const { action, agent } = modal;
      const index = state.rows.findIndex((r) => r.spid === agent.spid);

      // Optimistic: the row leaves this tab's filter immediately.
      dispatch({ type: 'ACTION_COMMIT', spid: agent.spid });
      try {
        await ACTION_VERBS[action](agent.spid, reason);
      } catch (err) {
        dispatch({
          type: 'ACTION_ROLLBACK',
          row: agent,
          index: index < 0 ? 0 : index,
          error: `Failed to ${ACTION_LABELS[action]} ${agent.displayName}: ${
            err instanceof Error ? err.message : 'Network error'
          }`,
        });
      }
    },
    [state.modal, state.rows]
  );

  // Defensive client-side filter — never show a row whose status doesn't
  // match the active tab, even if the API ignores the ?status= param.
  const visibleRows = state.rows.filter((r) => r.approvalStatus === state.tab);

  return (
    <main className="flex-1 p-6 space-y-5 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-violet-400" aria-hidden="true" />
            Business Agents
          </h1>
          <p className="text-zinc-500 text-sm mt-0.5">
            Review Independent Business Agent applications — approval unlocks accrued earnings
          </p>
        </div>

        {/* Tab filter */}
        <div className="flex gap-2 flex-wrap" role="group" aria-label="Filter by approval status">
          {TABS.map((tab) => {
            const isActive = !showHeld && state.tab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => {
                  setShowHeld(false);
                  dispatch({ type: 'SET_TAB', tab });
                }}
                className={[
                  'min-h-[48px] px-3 rounded text-sm transition-colors',
                  isActive
                    ? 'bg-violet-700 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200',
                ].join(' ')}
                aria-pressed={isActive}
              >
                {tab}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setShowHeld(true)}
            className={[
              'min-h-[48px] px-3 rounded text-sm transition-colors',
              showHeld
                ? 'bg-violet-700 text-white'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200',
            ].join(' ')}
            aria-pressed={showHeld}
          >
            Held earnings
          </button>
        </div>
      </div>

      {/* Action rollback banner */}
      {state.actionError && (
        <div
          className="flex items-start gap-3 p-3 rounded-lg bg-red-950 border border-red-800 text-red-300"
          role="alert"
        >
          <AlertTriangle size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs flex-1">{state.actionError}</p>
          <button
            type="button"
            onClick={() => dispatch({ type: 'CLEAR_ACTION_ERROR' })}
            className="text-red-400 hover:text-red-200"
            aria-label="Dismiss error"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Failed recruiter-override cascade after a reverse */}
      {cascadeWarnings.map((warning) => (
        <div
          key={warning.earning.earningId}
          className="flex items-start gap-3 p-4 rounded-lg bg-amber-950 border border-amber-800 text-amber-200"
          role="alert"
          aria-label="Override reversal failed"
        >
          <AlertTriangle size={18} className="shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex-1">
            <p className="font-medium text-sm">
              Override reversal failed for earning #{warning.earning.earningId}
            </p>
            <p className="text-xs text-amber-300 mt-0.5">{warning.message}</p>
            <button
              type="button"
              onClick={() => setReasonTarget({ kind: 'reverse', earning: warning.earning })}
              aria-label={`Retry override reversal for earning ${warning.earning.earningId}`}
              className="mt-2 min-h-[48px] px-3 rounded text-sm bg-amber-800 text-amber-50 hover:bg-amber-700 transition-colors"
            >
              Retry override reversal
            </button>
          </div>
          <button
            type="button"
            onClick={() =>
              setCascadeWarnings((prev) =>
                prev.filter((w) => w.earning.earningId !== warning.earning.earningId)
              )
            }
            aria-label={`Dismiss override warning for earning ${warning.earning.earningId}`}
            className="min-h-[48px] px-3 rounded text-sm text-amber-300 hover:text-amber-100"
          >
            Dismiss
          </button>
        </div>
      ))}

      {/* Failed release of the recruiter override that moves with its parent */}
      {releaseWarnings.map((warning) => (
        <div
          key={warning.earningId}
          className="flex items-start gap-3 p-4 rounded-lg bg-amber-950 border border-amber-800 text-amber-200"
          role="alert"
          aria-label="Override release failed"
        >
          <AlertTriangle size={18} className="shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex-1">
            <p className="font-medium text-sm">
              Override release failed for earning #{warning.earningId}
            </p>
            <p className="text-xs text-amber-300 mt-0.5">{warning.message}</p>
          </div>
          <button
            type="button"
            onClick={() =>
              setReleaseWarnings((prev) => prev.filter((w) => w.earningId !== warning.earningId))
            }
            aria-label={`Dismiss override release warning for earning ${warning.earningId}`}
            className="min-h-[48px] px-3 rounded text-sm text-amber-300 hover:text-amber-100"
          >
            Dismiss
          </button>
        </div>
      ))}

      <StandingOverridesPanel
        summary={standing.summary}
        loading={standing.loading}
        error={standing.error}
        onOpenAgent={(spid) => void openDetail(spid)}
      />

      {/* Held earnings tab */}
      {showHeld && (
        <>
          {held.loading && (
            <div
              className="flex items-center justify-center py-16 text-zinc-500"
              role="status"
              aria-label="Loading held earnings"
            >
              <RefreshCw className="w-5 h-5 mr-3 motion-safe:animate-spin" aria-hidden="true" />
              Loading held earnings…
            </div>
          )}
          {held.error && !held.loading && (
            <div
              className="flex items-start gap-3 p-4 rounded-lg bg-red-950 border border-red-800 text-red-300"
              role="alert"
            >
              <AlertTriangle size={18} className="shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="font-medium text-sm">Failed to load held earnings</p>
                <p className="text-xs text-red-400 mt-0.5">{held.error}</p>
                <button
                  type="button"
                  onClick={() => void loadHeld()}
                  className="mt-2 text-xs text-red-300 underline hover:text-red-100"
                >
                  Retry
                </button>
              </div>
            </div>
          )}
          {!held.error && (held.rows.length > 0 || !held.loading) && (
            <HeldEarningsTable
              rows={held.rows}
              onAct={(kind, earning) => setReasonTarget({ kind, earning })}
            />
          )}
        </>
      )}

      {/* Loading state */}
      {!showHeld && state.loading && (
        <div
          className="flex items-center justify-center py-16 text-zinc-500"
          role="status"
          aria-label="Loading"
        >
          <RefreshCw className="w-5 h-5 mr-3 motion-safe:animate-spin" aria-hidden="true" />
          Loading agents…
        </div>
      )}

      {/* Error state */}
      {!showHeld && state.error && !state.loading && (
        <div
          className="flex items-start gap-3 p-4 rounded-lg bg-red-950 border border-red-800 text-red-300"
          role="alert"
        >
          <AlertTriangle size={18} className="shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="font-medium text-sm">Failed to load business agents</p>
            <p className="text-xs text-red-400 mt-0.5">{state.error}</p>
            <button
              type="button"
              onClick={() => void loadAgents(state.tab, { cancelled: false })}
              className="mt-2 text-xs text-red-300 underline hover:text-red-100"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!showHeld && !state.loading && !state.error && visibleRows.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-zinc-500 gap-2">
          <CheckCircle size={40} className="text-zinc-700" aria-hidden="true" />
          <p className="text-sm">{EMPTY_COPY[state.tab]}</p>
        </div>
      )}

      {/* Data table */}
      {!showHeld && !state.loading && !state.error && visibleRows.length > 0 && (
        <div className="rounded-lg border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm" aria-label="Business agents">
            <thead>
              <tr className="bg-zinc-900 text-zinc-400 text-left text-xs uppercase tracking-wide">
                <th scope="col" className="px-4 py-3 font-medium">Agent</th>
                <th scope="col" className="px-4 py-3 font-medium">Referral Code</th>
                <th scope="col" className="px-4 py-3 font-medium">Applied</th>
                <th scope="col" className="px-4 py-3 font-medium">Locked Earnings</th>
                <th scope="col" className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <AgentRow
                  key={row.spid}
                  row={row}
                  tab={state.tab}
                  onAction={(action, agent) => dispatch({ type: 'OPEN_MODAL', action, agent })}
                  onDetails={(agent) => void openDetail(agent.spid)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirm dialog */}
      <AgentActionConfirmModal
        isOpen={state.modal !== null}
        action={state.modal?.action ?? 'approve'}
        agent={state.modal?.agent ?? null}
        onConfirm={handleConfirm}
        onCancel={() => dispatch({ type: 'CLOSE_MODAL' })}
      />

      {/* Detail modal */}
      {state.detail && (
        <AgentDetailModal
          detail={state.detail}
          onClose={() => dispatch({ type: 'DETAIL_CLOSE' })}
          onRetry={(spid) => void openDetail(spid)}
          onRerun={handleRerun}
          onClearLink={(spid) => setReasonTarget({ kind: 'clear', spid })}
          onWriteOff={handleWriteOff}
          onRetryOverride={(earning) => setReasonTarget({ kind: 'reverse', earning })}
          escapeDisabled={reasonTarget !== null}
        />
      )}

      {/* Reason dialog - Release / Reverse / Clear linked account */}
      {reasonTarget && (
        <ReasonDialog
          title={
            reasonTarget.kind === 'release'
              ? 'Release held earning?'
              : reasonTarget.kind === 'reverse'
                ? 'Reverse held earning?'
                : 'Clear linked account?'
          }
          description={
            reasonTarget.kind === 'clear'
              ? 'The agent is unlinked from their Chelless account. The reason is recorded in the audit history.'
              : `${reasonTarget.earning.agentName} — ${usd(reasonTarget.earning.amountUsd)}. The reason is recorded in the audit history.`
          }
          submitLabel={
            reasonTarget.kind === 'release'
              ? 'Release earning'
              : reasonTarget.kind === 'reverse'
                ? 'Reverse earning'
                : 'Clear link'
          }
          onSubmit={handleReasonSubmit}
          onCancel={() => setReasonTarget(null)}
          renderError={reasonTarget.kind === 'clear' ? renderClearError : undefined}
        />
      )}
    </main>
  );
}
